import { createContext, useContext } from "react";

import type { ModuleDefinition, Panel } from "./module";
import type { PanelMe } from "./session";

export interface NavGroupConfig {
  /** "main" is the ungrouped top section. */
  id: string;
  /** i18n key for the heading; omit for "main". */
  label?: string;
}

export interface AuthCopy {
  /** i18n keys for the brand panel of the login screens. */
  headline: string;
  subheadline: string;
  points: string[];
}

export interface PanelAppConfig {
  panel: Panel;
  /** Badge next to the logo: "ADMIN", "VENDOR". */
  product: string;
  /** Frontend modules bundled in this app, in dependency order. */
  modules: ModuleDefinition[];
  /** Sidebar group order. Groups without items are hidden. */
  navGroups: NavGroupConfig[];
  authCopy: AuthCopy;
  /**
   * Panel-wide rule run on every protected page, e.g. "admins must set up 2FA first" or
   * "vendors must finish onboarding". Return a path to redirect to, or null.
   */
  guard?: (me: PanelMe, pathname: string) => string | null;
  loginPath?: string;
  homePath?: string;
  /** localStorage key prefix (sidebar state etc.). */
  storageKey?: string;
}

export const AppConfigContext = createContext<PanelAppConfig | null>(null);

export function useAppConfig(): PanelAppConfig {
  const value = useContext(AppConfigContext);
  if (!value) throw new Error("useAppConfig() must be used inside createPanelApp().");
  return value;
}
